import React from 'react';
import { LazyMotion, domAnimation, m } from "framer-motion";
import './OrangeSpinner.css'
import { useRef, useEffect, useState } from 'react';
import * as THREE from 'three';
import gsap from 'gsap';
import { useTranslation } from 'react-i18next';

const EducationalOrangeSpinner = () => {
  const [progress, setProgress] = useState(0);
  const [currentText, setCurrentText] = useState(0);
  
  const containerVariants = {
    start: { opacity: 0, scale: 0.8, y: 50 },
    end: { 
      opacity: 1, 
      scale: 1, 
      y: 0,
      transition: { 
        duration: 0.8, 
        ease: "easeOut",
        staggerChildren: 0.2
      } 
    }
  };

  const bookVariants = {
    start: { opacity: 0, y: 30, rotateY: -90, scale: 0.5 },
    end: { 
      opacity: 1, 
      y: 0, 
      rotateY: 0, 
      scale: 1,
      transition: { 
        duration: 1, 
        ease: "easeOut",
        type: "spring",
        stiffness: 100
      } 
    }
  };

  const penVariants = {
    start: { opacity: 0, x: -50, rotate: -45, scale: 0.5 },
    end: { 
      opacity: 1, 
      x: 0, 
      rotate: 0, 
      scale: 1,
      transition: { 
        duration: 0.8, 
        delay: 0.3, 
        ease: "easeOut",
        type: "spring",
        stiffness: 120
      } 
    }
  };

  const lightbulbVariants = {
    start: { opacity: 0, scale: 0, rotate: -180, y: 30 },
    end: { 
      opacity: 1, 
      scale: 1, 
      rotate: 0, 
      y: 0,
      transition: { 
        duration: 1, 
        delay: 0.5, 
        ease: "easeOut",
        type: "spring",
        stiffness: 80
      } 
    }
  };

  const capVariants = {
    start: { opacity: 0, y: 40, rotateZ: 45, scale: 0.5 },
    end: { 
      opacity: 1, 
      y: 0, 
      rotateZ: 0, 
      scale: 1,
      transition: { 
        duration: 0.9, 
        delay: 0.7, 
        ease: "easeOut",
        type: "spring",
        stiffness: 90
      } 
    }
  };

  const textVariants = {
    start: { opacity: 0, y: 20, scale: 0.8 },
    end: { 
      opacity: 1, 
      y: 0, 
      scale: 1,
      transition: { 
        duration: 0.6, 
        delay: 1,
        ease: "easeOut"
      } 
    }
  };

  const canvasRef = useRef();
  const { t } = useTranslation('global');

  // النصوص التعليمية المتغيرة
  const loadingTexts = [
    "جاري تحميل المحتوى التعليمي...",
    "إعداد الدروس والتمارين...",
    "تحضير المواد التفاعلية...",
    "جاري إعداد الاختبارات...",
    "تحميل الفيديوهات التعليمية..."
  ];

  useEffect(() => {
    // تحديث النص كل 2 ثانية
    const textInterval = setInterval(() => {
      setCurrentText((prev) => (prev + 1) % loadingTexts.length);
    }, 2000);

    // محاكاة التقدم
    const progressInterval = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 95) return prev;
        return prev + Math.random() * 5;
      });
    }, 500);

    return () => {
      clearInterval(textInterval);
      clearInterval(progressInterval);
    };
  }, []);

  useEffect(() => {
    // إعداد مشهد النجوم التعليمية المتقدم
    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(75, window.innerWidth / window.innerHeight, 0.1, 1000);
    camera.position.z = 80;
    const renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true });
    renderer.setSize(window.innerWidth, window.innerHeight);
    renderer.setClearColor(0x000000, 0);
    renderer.setPixelRatio(window.devicePixelRatio);
    
    if (canvasRef.current) {
      canvasRef.current.appendChild(renderer.domElement);
    }

    // نجوم تعليمية بألوان مختلفة وأحجام متغيرة
    const starsGeometry = new THREE.BufferGeometry();
    const starCount = 80; // تقليل عدد النجوم للخلفية الخفيفة
    const positions = [];
    const colors = [];
    const sizes = [];
    
    for (let i = 0; i < starCount; i++) {
      positions.push(
        (Math.random() - 0.5) * window.innerWidth * 1.5,
        (Math.random() - 0.5) * window.innerHeight * 1.5,
        Math.random() * -400
      );
      
      // ألوان تعليمية متنوعة مع تأثيرات خفيفة
      const colorChoices = [
        0x00BCD4, 0x2196F3, 0x3F51B5, 0x9C27B0, 0x673AB7,
        0x4CAF50, 0x8BC34A, 0xCDDC39, 0xFFEB3B, 0x607D8B
      ];
      const color = colorChoices[Math.floor(Math.random() * colorChoices.length)];
      colors.push(color, color, color);
      
      // أحجام مختلفة للنجوم - خفيفة جداً
      sizes.push(Math.random() * 2 + 0.5); // نجوم صغيرة جداً
    }
    
    starsGeometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
    starsGeometry.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3));
    starsGeometry.setAttribute('size', new THREE.Float32BufferAttribute(sizes, 1));
    
    const starsMaterial = new THREE.PointsMaterial({ 
      size: 2, // نجوم صغيرة جداً
      vertexColors: true,
      transparent: true,
      opacity: 0.2, // شفافية عالية جداً
      blending: THREE.AdditiveBlending,
      depthWrite: false
    });
    
    const stars = new THREE.Points(starsGeometry, starsMaterial);
    scene.add(stars);

    // حركة النجوم البطيئة والخفيفة
    gsap.to(stars.rotation, { 
      y: Math.PI * 2, 
      duration: 60, // حركة بطيئة جداً
      repeat: -1, 
      ease: 'linear' 
    });

    gsap.to(stars.rotation, { 
      x: Math.PI * 0.3, 
      duration: 80, // حركة بطيئة جداً
      repeat: -1, 
      ease: 'linear' 
    });

    // تأثير نبض خفيف للنجوم
    gsap.to(starsMaterial, {
      opacity: 0.1,
      duration: 4,
      repeat: -1,
      yoyo: true,
      ease: "sine.inOut"
    });

    let running = true;
    function animate() {
      if (!running) return;
      renderer.render(scene, camera);
      requestAnimationFrame(animate);
    }
    animate();

    // تنظيف عند إغلاق المكون
    return () => {
      running = false;
      renderer.dispose();
      if (canvasRef.current) {
        canvasRef.current.innerHTML = '';
      }
    };
  }, []);

  return (
    <div className="educational-spinner-overlay" style={{ 
      position: 'fixed', 
      inset: 0, 
      zIndex: 9999, 
      background: 'rgba(30, 58, 138, 0.15)',
      backdropFilter: 'blur(2px)',
      overflow: 'hidden'
    }}>
      {/* نجوم متحركة في الـ overlay */}
      <div className="overlay-stars">
        <div className="overlay-star"></div>
        <div className="overlay-star"></div>
        <div className="overlay-star"></div>
        <div className="overlay-star"></div>
        <div className="overlay-star"></div>
        <div className="overlay-star"></div>
        <div className="overlay-star"></div>
        <div className="overlay-star"></div>
        <div className="overlay-star"></div>
        <div className="overlay-star"></div>
        <div className="overlay-star"></div>
        <div className="overlay-star"></div>
      </div>

      {/* نجوم متحركة حول اللودر */}
      <div className="spinner-stars">
        <div className="spinner-star"></div>
        <div className="spinner-star"></div>
        <div className="spinner-star"></div>
        <div className="spinner-star"></div>
        <div className="spinner-star"></div>
        <div className="spinner-star"></div>
        <div className="spinner-star"></div>
        <div className="spinner-star"></div>
        <div className="spinner-star"></div>
        <div className="spinner-star"></div>
        <div className="spinner-star"></div>
        <div className="spinner-star"></div>
        <div className="spinner-star"></div>
        <div className="spinner-star"></div>
        <div className="spinner-star"></div>
        <div className="spinner-star"></div>
      </div>

      {/* خلفية النجوم */}
      <div ref={canvasRef} style={{ 
        position: 'absolute', 
        inset: 0, 
        width: '100vw', 
        height: '100vh', 
        zIndex: 0, 
        pointerEvents: 'none' 
      }} />
      
      {/* تأثيرات إضافية في الخلفية */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'transparent',
        zIndex: 1
      }} />
      
      <LazyMotion features={domAnimation}>
        <m.div
          initial="start"
          animate="end"
          variants={containerVariants}
          className="educational-spinner-container"
          style={{ 
            position: 'relative', 
            zIndex: 2,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100vh',
            padding: '20px'
          }}
        >
          {/* العناصر التعليمية المحسنة */}
          <div style={{ 
            position: 'relative', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            marginBottom: '40px',
            flexWrap: 'wrap',
            gap: '30px'
          }}>
            {/* الكتاب المحسن */}
            <m.div
              variants={bookVariants}
              style={{
                width: '90px',
                height: '70px',
                background: 'linear-gradient(135deg, #00BCD4, #26C6DA, #4DD0E1)',
                borderRadius: '12px',
                position: 'relative',
                boxShadow: '0 8px 25px rgba(0, 188, 212, 0.3), 0 0 15px rgba(0, 188, 212, 0.2)',
                transformStyle: 'preserve-3d',
                cursor: 'pointer'
              }}
              animate={{ 
                rotateY: [0, 15, 0, -15, 0],
                y: [0, -5, 0],
                scale: [1, 1.02, 1]
              }}
              transition={{ 
                duration: 4, 
                repeat: Infinity, 
                ease: "easeInOut" 
              }}
              whileHover={{ scale: 1.05, boxShadow: '0 10px 30px rgba(0, 188, 212, 0.4)' }}
            >
              {/* صفحات الكتاب المحسنة */}
              <div style={{
                position: 'absolute',
                top: '6px',
                left: '6px',
                right: '6px',
                bottom: '6px',
                background: 'linear-gradient(135deg, #FFF, #F8F9FA)',
                borderRadius: '8px',
                border: '2px solid #E0E0E0',
                boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.1)'
              }} />
              <div style={{
                position: 'absolute',
                top: '10px',
                left: '10px',
                right: '10px',
                bottom: '10px',
                background: 'linear-gradient(135deg, #FFF, #F8F9FA)',
                borderRadius: '4px',
                border: '1px solid #E0E0E0'
              }} />
              {/* أيقونة الكتاب */}
              <div style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                fontSize: '24px',
                zIndex: 2
              }}>
                📚
              </div>
            </m.div>

            {/* القلم المحسن */}
            <m.div
              variants={penVariants}
              style={{
                width: '6px',
                height: '60px',
                background: 'linear-gradient(to bottom, #2196F3, #1976D2, #1565C0)',
                position: 'relative',
                borderRadius: '3px',
                transformOrigin: 'bottom center',
                boxShadow: '0 3px 12px rgba(33, 150, 243, 0.3)',
                cursor: 'pointer'
              }}
              animate={{ 
                rotate: [0, 5, 0, -5, 0],
                y: [0, -2, 0],
                scale: [1, 1.01, 1]
              }}
              transition={{ 
                duration: 3, 
                repeat: Infinity, 
                ease: "easeInOut" 
              }}
              whileHover={{ scale: 1.05, boxShadow: '0 4px 15px rgba(33, 150, 243, 0.4)' }}
            >
              {/* رأس القلم المحسن */}
              <div style={{
                width: '12px',
                height: '12px',
                background: 'linear-gradient(135deg, #9C27B0, #7B1FA2)',
                position: 'absolute',
                top: '-6px',
                left: '-3px',
                borderRadius: '50%',
                boxShadow: '0 2px 6px rgba(156, 39, 176, 0.4)'
              }} />
              {/* قاعدة القلم */}
              <div style={{
                width: '8px',
                height: '8px',
                background: '#607D8B',
                position: 'absolute',
                bottom: '-4px',
                left: '-1px',
                borderRadius: '50%'
              }} />
            </m.div>

            {/* المصباح الكهربائي المحسن */}
            <m.div
              variants={lightbulbVariants}
              style={{
                width: '50px',
                height: '60px',
                background: 'linear-gradient(135deg, #4CAF50, #66BB6A, #81C784)',
                position: 'relative',
                borderRadius: '25px 25px 8px 8px',
                boxShadow: '0 0 20px rgba(76, 175, 80, 0.5), 0 0 30px rgba(76, 175, 80, 0.3)',
                cursor: 'pointer'
              }}
              animate={{ 
                scale: [1, 1.1, 1],
                boxShadow: [
                  '0 0 20px rgba(76, 175, 80, 0.5), 0 0 30px rgba(76, 175, 80, 0.3)',
                  '0 0 25px rgba(76, 175, 80, 0.7), 0 0 35px rgba(76, 175, 80, 0.4)',
                  '0 0 20px rgba(76, 175, 80, 0.5), 0 0 30px rgba(76, 175, 80, 0.3)'
                ],
                y: [0, -3, 0]
              }}
              transition={{ 
                duration: 2.5, 
                repeat: Infinity, 
                ease: "easeInOut" 
              }}
              whileHover={{ scale: 1.1, boxShadow: '0 0 30px rgba(76, 175, 80, 0.7), 0 0 40px rgba(76, 175, 80, 0.5)' }}
            >
              {/* قاعدة المصباح المحسنة */}
              <div style={{
                width: '25px',
                height: '10px',
                background: 'linear-gradient(135deg, #607D8B, #455A64)',
                position: 'absolute',
                bottom: '-10px',
                left: '12.5px',
                transform: 'translateX(-50%)',
                borderRadius: '0 0 12px 12px',
                boxShadow: '0 2px 4px rgba(0,0,0,0.3)'
              }} />
              {/* أيقونة المصباح */}
              <div style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                fontSize: '20px',
                zIndex: 2
              }}>
                💡
              </div>
            </m.div>

            {/* قبعة التخرج المحسنة */}
            <m.div
              variants={capVariants}
              style={{
                width: '80px',
                height: '55px',
                background: 'linear-gradient(135deg, #9C27B0, #BA68C8, #CE93D8)',
                borderRadius: '50% 50% 0 0',
                position: 'relative',
                boxShadow: '0 6px 20px rgba(156, 39, 176, 0.4), 0 0 12px rgba(156, 39, 176, 0.2)',
                cursor: 'pointer'
              }}
              animate={{ 
                y: [-3, 3, -3],
                rotateZ: [0, 2, 0, -2, 0],
                scale: [1, 1.02, 1]
              }}
              transition={{ 
                duration: 3.5, 
                repeat: Infinity, 
                ease: "easeInOut" 
              }}
              whileHover={{ scale: 1.05, boxShadow: '0 8px 25px rgba(156, 39, 176, 0.5), 0 0 15px rgba(156, 39, 176, 0.3)' }}
            >
              {/* الخيط */}
              <div style={{
                width: '2px',
                height: '20px',
                background: '#7B1FA2',
                position: 'absolute',
                bottom: '-20px',
                left: '50%',
                transform: 'translateX(-50%)',
                borderRadius: '1px'
              }} />
              {/* أيقونة القبعة */}
              <div style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                fontSize: '22px',
                zIndex: 2
              }}>
                🎓
              </div>
            </m.div>
          </div>
          
          {/* النص المحسن */}
          <m.div 
            variants={textVariants}
            style={{ 
              textAlign: 'center',
              color: '#ffffff',
              fontSize: '20px',
              fontWeight: '700',
              textShadow: '0 2px 4px rgba(0,0,0,0.3)',
              marginBottom: '25px',
              minHeight: '60px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
            animate={{ 
              opacity: [0.8, 1, 0.8],
              y: [0, -2, 0],
              scale: [1, 1.01, 1]
            }}
            transition={{ 
              duration: 2.5, 
              repeat: Infinity,
              ease: "easeInOut"
            }}
          >
            {loadingTexts[currentText]}
          </m.div>

          {/* شريط التقدم المحسن */}
          <m.div
            style={{
              width: '300px',
              height: '6px',
              background: 'rgba(255, 255, 255, 0.1)',
              borderRadius: '3px',
              marginTop: '20px',
              overflow: 'hidden',
              boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.1)',
              position: 'relative'
            }}
          >
            <m.div
              style={{
                width: `${progress}%`,
                height: '100%',
                background: 'linear-gradient(90deg, #00BCD4, #2196F3, #9C27B0, #4CAF50, #3F51B5)',
                borderRadius: '3px',
                boxShadow: '0 0 8px rgba(0, 188, 212, 0.4)',
                position: 'relative'
              }}
              animate={{
                background: [
                  'linear-gradient(90deg, #00BCD4, #2196F3, #9C27B0, #4CAF50, #3F51B5)',
                  'linear-gradient(90deg, #3F51B5, #00BCD4, #2196F3, #9C27B0, #4CAF50)',
                  'linear-gradient(90deg, #00BCD4, #2196F3, #9C27B0, #4CAF50, #3F51B5)'
                ]
              }}
              transition={{
                duration: 3,
                repeat: Infinity,
                ease: "linear"
              }}
            />
            {/* مؤشر التقدم */}
            <div style={{
              position: 'absolute',
              top: '50%',
              left: `${progress}%`,
              transform: 'translate(-50%, -50%)',
              width: '10px',
              height: '10px',
              background: 'rgba(255, 255, 255, 0.8)',
              borderRadius: '50%',
              boxShadow: '0 0 8px rgba(255,255,255,0.4)',
              zIndex: 2
            }} />
          </m.div>

          {/* نسبة التقدم */}
          <m.div
            style={{
              marginTop: '15px',
              fontSize: '16px',
              fontWeight: '600',
              color: '#ffffff',
              textShadow: '0 1px 2px rgba(0,0,0,0.3)'
            }}
            animate={{
              opacity: [0.7, 1, 0.7]
            }}
            transition={{
              duration: 1.5,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          >
            {Math.round(progress)}%
          </m.div>
        </m.div>
      </LazyMotion>
    </div>
  );
};

export default EducationalOrangeSpinner;
